import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, User, Image as ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPanel = () => {
  const { currentProject, apiStatus, refreshAssets, setBrandKit } = useProject();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load chat history
  useEffect(() => {
    if (currentProject?.id) {
      loadHistory();
    }
  }, [currentProject?.id]);

  // Auto scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/chat/${currentProject.id}/history`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    if (!apiStatus.openai) {
      toast.error('No text AI provider configured. Add GROQ_API_KEY (free), DEEPSEEK_API_KEY, or OPENAI_API_KEY to backend/.env');
      return;
    }

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        project_id: currentProject.id,
        message: userMessage.content
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        asset_url: response.data.asset_url,
        asset_type: response.data.asset_type,
        generated_by: response.data.generated_by
      };

      setMessages(prev => [...prev, assistantMessage]);

      // If an image was generated, refresh assets
      if (response.data.asset_url) {
        refreshAssets();
        toast.success('New asset generated!');
      }

      // If brand kit was updated
      if (response.data.brand_kit_update) {
        toast.info('Brand kit suggestion received');
      }

    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send message';
      toast.error(errorMsg);
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${errorMsg}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedPrompts = [
    "Create a brand identity for a modern coffee shop",
    "Generate a minimalist logo for my tech startup",
    "Suggest a color palette for a luxury fashion brand",
    "Design a social media banner for a fitness app"
  ];

  return (
    <div className="h-full flex flex-col" data-testid="chat-panel">
      {/* Messages Area */}
      <ScrollArea className="flex-1 p-6" ref={scrollRef}>
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Welcome message */}
          {messages.length === 0 && !loadingHistory && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl text-foreground mb-3">
                Hi! I'm OMNICREATE
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Your AI design assistant. Tell me about your brand and what you'd like to create.
              </p>
              
              {/* Suggested prompts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                {suggestedPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(prompt)}
                    className="text-left p-4 rounded-xl bg-black/[0.03] border border-border text-sm text-foreground/80 hover:bg-black/[0.06] hover:border-primary/30 transition-all"
                    data-testid={`suggested-prompt-${index}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Loading history */}
          {loadingHistory && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          )}

          {/* Messages */}
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : ''}`}
                data-testid={`message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                )}
                
                <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                  <div
                    className={`rounded-2xl px-5 py-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : message.isError
                        ? 'bg-red-500/10 border border-red-500/20 text-red-700'
                        : 'bg-black/[0.03] border border-border text-foreground'
                    }`}
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                    
                    {/* Generated image preview */}
                    {message.asset_url && (
                      <div className="mt-4 rounded-lg overflow-hidden border border-border">
                        <img 
                          src={message.asset_url} 
                          alt="Generated asset"
                          className="w-full max-w-sm"
                          data-testid={`generated-image-${index}`}
                        />
                        <div className="p-2 bg-black/[0.03] flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-primary" />
                          <span className="text-xs text-muted-foreground">{message.asset_type || 'Generated'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/70 mt-2 px-2 flex items-center gap-2">
                    {new Date(message.timestamp).toLocaleTimeString()}
                    {message.role === 'assistant' && <ProviderBadge provider={message.generated_by} />}
                  </p>
                </div>

                {message.role === 'user' && (
                  <div className="w-10 h-10 rounded-xl bg-black/[0.06] flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Loading indicator */}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-4"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="bg-black/[0.03] border border-border rounded-2xl px-5 py-4">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* API Warning */}
      {!apiStatus.openai && (
        <div className="px-6 py-3 bg-yellow-500/10 border-t border-yellow-500/20">
          <div className="flex items-center gap-2 text-yellow-700 text-sm max-w-3xl mx-auto">
            <AlertCircle className="w-4 h-4" />
            <span>No text AI provider configured. Add GROQ_API_KEY (free), DEEPSEEK_API_KEY, or OPENAI_API_KEY to backend/.env</span>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-container p-6 border-t border-border">
        <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
          <div className="relative">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={apiStatus.openai ? "Describe what you want to create..." : "Configure a text AI provider to enable chat..."}
              className="w-full min-h-[60px] max-h-[200px] pr-14 bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground rounded-xl resize-none focus:ring-primary"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={loading || !input.trim() || !apiStatus.openai}
              className="absolute right-3 bottom-3 bg-primary hover:bg-primary/90 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </form>
      </div>
    </div>
  );
};

export default ChatPanel;
